import { useEffect, useRef, useState } from "react";
import { LuRefreshCcw } from "react-icons/lu";
import {
  HistoryLoadingStatus,
  useHistoryLoadingStatus,
  useProjectActions,
  useProjectId,
  useProjectMessages,
} from "../../../core_stores/useProjectStore";
import { typography } from "../../../design/tokens";

const MINIMUM_DISPLAY_THRESHOLD_MS = 500;
const EXTENDED_LOADING_THRESHOLD_MS = 15_000; // 15 seconds

export default function ProjectChatHistoryLoadingIndicator() {
  const history = useProjectMessages();
  const historyLoadingStatus = useHistoryLoadingStatus();
  const projectId = useProjectId();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);
  const loadingStartTimeRef = useRef<number | null>(null);
  const hasLoadingStartedRef = useRef(false);

  const isLoading =
    historyLoadingStatus === HistoryLoadingStatus.LOADING &&
    (history === null || history.length === 0);

  const isError =
    historyLoadingStatus === HistoryLoadingStatus.IDLE &&
    history !== null &&
    history.length === 0 &&
    projectId !== null &&
    hasLoadingStartedRef.current;

  const shouldShowIndicator = isLoading || isError;

  // Track that loading has started at least once
  useEffect(() => {
    if (isLoading) {
      hasLoadingStartedRef.current = true;
    }
  }, [isLoading]);

  // Reset the flag when project changes
  useEffect(() => {
    hasLoadingStartedRef.current = false;
  }, [projectId]);

  useEffect(() => {
    if (shouldShowIndicator) {
      // Track when loading/error starts
      if (loadingStartTimeRef.current === null) {
        loadingStartTimeRef.current = Date.now();
      }

      // Only show indicator if it has been active for at least MINIMUM_DISPLAY_THRESHOLD_MS
      const thresholdTimer = setTimeout(() => {
        const duration = Date.now() - (loadingStartTimeRef.current || 0);
        if (duration >= MINIMUM_DISPLAY_THRESHOLD_MS) {
          setShouldRender(true);
          const showTimer = setTimeout(() => setIsVisible(true), 50);
          return () => clearTimeout(showTimer);
        }
      }, MINIMUM_DISPLAY_THRESHOLD_MS);

      return () => clearTimeout(thresholdTimer);
    } else {
      // Reset when indicator should hide
      loadingStartTimeRef.current = null;
      setIsVisible(false);
      setShowExtendedMessage(false);
      const hideTimer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(hideTimer);
    }
  }, [shouldShowIndicator]);

  // Show extended message if loading takes longer than 15 seconds
  useEffect(() => {
    if (isLoading) {
      const extendedTimer = setTimeout(() => {
        setShowExtendedMessage(true);
      }, EXTENDED_LOADING_THRESHOLD_MS);

      return () => clearTimeout(extendedTimer);
    } else {
      setShowExtendedMessage(false);
    }
  }, [isLoading]);

  if (!shouldRender) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="relative pointer-events-auto">
        {/* Pulsing glow effect layer */}
        <div
          className={`absolute inset-0 rounded-lg pointer-events-none transition-all duration-200 ease-out ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          style={{
            boxShadow:
              "0 0 30px rgba(233, 30, 99, 0.4), 0 0 60px rgba(156, 39, 176, 0.3), 0 0 90px rgba(59, 130, 246, 0.2)",
            animation: "pulse-intensity 1.5s ease-in-out infinite",
          }}
        />

        {/* Content box */}
        <div
          className={`relative z-10 flex items-end gap-1 py-4 px-6 rounded-lg
                bg-dark-surface-primary
                border border-dark-placeholder
                transition-all duration-200 ease-out ${
                  isVisible
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 translate-y-2"
                }`}
        >
          {isError ? (
            <HistoryNotLoadedContent />
          ) : (
            <LoadingContent showExtendedMessage={showExtendedMessage} />
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryNotLoadedContent() {
  const projectId = useProjectId();
  const { initialize: initializeProject, dispose: disposeProject } =
    useProjectActions();

  const handleTryAgain = () => {
    const actualProjectId = projectId;
    disposeProject();
    initializeProject(actualProjectId!, null, null);
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <span>History cannot be loaded...</span>
      <button
        id="project-chat-history-loading-indicator-try-again"
        className="flex items-center gap-2
      bg-primary-blue text-white px-4 py-2 rounded-md
      hover:brightness-150 transition-all duration-300"
        onClick={handleTryAgain}
      >
        <LuRefreshCcw className="w-4 h-4" />
        Try again
      </button>
    </div>
  );
}

function LoadingContent({
  showExtendedMessage,
}: {
  showExtendedMessage: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className={`${typography.size.sm} ${typography.weight.medium}`}>
        Loading project...
      </span>
      {showExtendedMessage && (
        <span
          className={`${typography.size.xs} text-dark-text-secondary text-center`}
        >
          Large projects may take a bit longer.
          <br />
          Thanks for your patience!
        </span>
      )}
    </div>
  );
}
